import { useEffect, useMemo, useState } from 'react';
import { useSettings } from '../../context/SettingsContext';

const ALLOWED_EMAIL = 'programacionsebastian94@gmail.com';

const GlobalAnnouncementView = ({ onBack, currentUser }) => {
  const { settings, saveSettings, savingSettings } = useSettings();
  const [form, setForm] = useState({
    enabled: false,
    title: '',
    message: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const canManage = useMemo(() => {
    const email = (currentUser?.email || '').trim().toLowerCase();
    const username = (currentUser?.username || '').trim().toLowerCase();
    return email === ALLOWED_EMAIL || username === ALLOWED_EMAIL;
  }, [currentUser]);

  useEffect(() => {
    setForm({
      enabled: !!settings?.globalNoticeEnabled,
      title: settings?.globalNoticeTitle || '',
      message: settings?.globalNoticeMessage || '',
    });
  }, [settings]);

  const handleSave = async () => {
    setError('');
    setSuccess('');
    if (form.enabled && !form.message.trim()) {
      setError('Escribe un mensaje antes de activarlo.');
      return;
    }
    try {
      await saveSettings({
        globalNoticeEnabled: form.enabled,
        globalNoticeTitle: form.title.trim() || 'Aviso importante',
        globalNoticeMessage: form.message.trim(),
        globalNoticeUpdatedBy: currentUser?.email || ALLOWED_EMAIL,
        globalNoticeUpdatedAt: new Date().toLocaleString(),
      });
      setSuccess(form.enabled ? 'Aviso activado para todos.' : 'Aviso desactivado.');
    } catch (e) {
      console.error(e);
      setError('No se pudo guardar. Revisa tu conexión.');
    }
  };

  if (!canManage) {
    return (
      <div className="p-6 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
        <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-sm p-6 border border-slate-200">
          <p className="text-sm text-slate-700">
            Este módulo solo está disponible para la cuenta autorizada ({ALLOWED_EMAIL}).
          </p>
          <div className="mt-4">
            <button
              onClick={onBack}
              className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50"
            >
              Volver
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
      <div className="flex justify-between items-center mb-8">
        <button
          onClick={onBack}
          className="flex items-center text-blue-600 hover:text-blue-800 transition duration-200 px-4 py-2 rounded-lg border border-blue-200 hover:border-blue-400"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Volver al Menú
        </button>
        <h1 className="text-3xl font-extrabold text-gray-900">Aviso Global</h1>
        <div className="w-32 text-right text-xs text-emerald-700">Solo tú lo ves</div>
      </div>

      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-lg border border-slate-200 p-8 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-600">Muestra un mensaje a todos los usuarios al entrar.</p>
            {settings?.globalNoticeUpdatedAt && (
              <p className="text-xs text-slate-500 mt-1">
                Última edición: {settings.globalNoticeUpdatedAt} {settings.globalNoticeUpdatedBy ? `por ${settings.globalNoticeUpdatedBy}` : ''}
              </p>
            )}
          </div>
          <label className="inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={form.enabled}
              onChange={(e) => setForm((p) => ({ ...p, enabled: e.target.checked }))}
            />
            <div className="w-12 h-7 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:bg-emerald-500 transition-all relative">
              <div className={`absolute top-1 left-1 h-5 w-5 rounded-full bg-white transition-transform ${form.enabled ? 'translate-x-5' : ''}`} />
            </div>
            <span className="ml-3 text-sm font-semibold text-slate-800">
              {form.enabled ? 'Activo' : 'Inactivo'}
            </span>
          </label>
        </div>

        {error && <div className="rounded-lg bg-red-50 text-red-700 px-4 py-3 text-sm">{error}</div>}
        {success && <div className="rounded-lg bg-emerald-50 text-emerald-700 px-4 py-3 text-sm">{success}</div>}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Título</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
              placeholder="Ej: Aviso importante"
              className="w-full rounded-lg border border-slate-200 px-4 py-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Mensaje</label>
            <textarea
              rows={6}
              value={form.message}
              onChange={(e) => setForm((p) => ({ ...p, message: e.target.value }))}
              placeholder="Escribe el anuncio que verán todos los usuarios al entrar."
              className="w-full rounded-lg border border-slate-200 px-4 py-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            />
            <p className="text-xs text-slate-500 mt-1">
              Soporta saltos de línea. Solo tú puedes cambiarlo.
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => setForm((p) => ({ ...p, enabled: false }))}
            className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50"
          >
            Apagar
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={savingSettings}
            className="px-6 py-2 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-60"
          >
            {savingSettings ? 'Guardando...' : 'Guardar aviso'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GlobalAnnouncementView;
